import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { AngularFireStorage } from '@angular/fire/storage';
import { FlashMessagesService } from '@mobilestar/ngx-flash-messages';
import { LoaderService } from '@services/support/loader.service';
import { ModalService } from '@services/support/modal.service';
import { Subscription } from 'rxjs';

import { create as contentDisposition } from "content-disposition-header";

@Component({
  selector: 'app-downloads-config',
  templateUrl: './downloads-config.component.html',
  styleUrls: ['./downloads-config.component.scss']
})
export class DownloadsConfigComponent implements OnInit, OnDestroy {

  @ViewChild("addEnvironmentTemplate", { static: true }) private addEnvironmentTemplate: TemplateRef<any>;
  @ViewChild("addLinkTemplate", { static: true }) private addLinkTemplate: TemplateRef<any>;

  @ViewChild("deleteEnvironmentTemplate", { static: true }) private deleteEnvironmentTemplate: TemplateRef<any>;
  @ViewChild("deleteLinkTemplate", { static: true }) private deleteLinkTemplate: TemplateRef<any>;

  @ViewChild("uploadTemplate", { static: true }) private uploadTemplate: TemplateRef<any>;
  @ViewChild("downloadTemplate", { static: true }) private downloadTemplate: TemplateRef<any>;

  @ViewChild("showUrlTemplate", { static: true }) private showUrlTemplate: TemplateRef<any>;

  environmentList = []
  environmentListSub: Subscription = null

  constructor(
    private afdb: AngularFireDatabase,
    private afst: AngularFireStorage,
    private loaderService: LoaderService,
    private modalService: ModalService,
    private flashMessages: FlashMessagesService
  ) { }

  ngOnInit() {
    this.environmentListSub = this.afdb.object<any>("download_links/environments").valueChanges().subscribe(data => {
      const envIds = Object.keys(data)
      this.environmentList = envIds.map(eid => {
        const env = data[eid]
        env.id = eid
        return env
      })

      for (const env of this.environmentList) {
        const lIds = Object.keys(env.links ? env.links : {})
        const links = lIds.map(lid => {
          const lnk = env.links[lid]
          lnk.id = lid
          return lnk
        })
        env.links = links
      }
    })
  }

  ngOnDestroy(): void {
    if (this.environmentListSub) { this.environmentListSub.unsubscribe() }
  }

  addEnv() {
    const dataModel = { envName: '' }
    const modalId = this.modalService.show({
      template: this.addEnvironmentTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          add: () => {
            this.loaderService.show()
            this.afdb.list(`download_links/environments`).push({ name: dataModel.envName })
              .then(() => this.flashMessages.show('Environment added', { cssClass: 'alert-success', timeout: 4000, closeOnClick: true }))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('An error occurred while adding environment', { cssClass: 'alert-success', timeout: 4000, closeOnClick: true })
              })
              .finally(() => {
                this.loaderService.hide()
                this.modalService.hide(modalId)
              })
          }
        }
      }
    })
  }

  deleteEnv(envId: string, links: any) {
    const modalId = this.modalService.show({
      template: this.deleteEnvironmentTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          delete: () => {
            if (links) {
              const promises = []
              for (const lid of Object.keys(links)) {
                promises.push(this.afst.ref(`download_links/${envId}/${lid}`).delete().toPromise())
              }
              this.loaderService.show()
              Promise.all(promises)
                .then(() => this.afdb.object(`download_links/environments/${envId}`).remove())
                .catch(err => {
                  console.log(err)
                  this.flashMessages.show('Delete Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
                })
                .finally(() => {
                  this.loaderService.hide()
                  this.modalService.hide(modalId)
                })
            } else {
              this.loaderService.show()
              this.afdb.object(`download_links/environments/${envId}`).remove()
                .catch(err => {
                  console.log(err)
                  this.flashMessages.show('Delete Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
                })
                .finally(() => {
                  this.loaderService.hide()
                  this.modalService.hide(modalId)
                })
            }
          }
        }
      }
    })
  }

  replaceLnk(envId: string, lnk: any) {
    this.showUploadFileModal(envId, lnk.id, "replace", lnk)
  }

  async showActualDownloadUrl(lnk: any) {
    this.loaderService.show()
    const url = await (this.afst.ref(lnk.filePath).getDownloadURL().toPromise())
    this.loaderService.hide()

    const dataModel = { url: url }
    const modalId = this.modalService.show({
      template: this.showUrlTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
        }
      }
    });
  }

  copied(event) {
    this.flashMessages.show("Copied to clipboard.", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
  }

  addLnk(envId: string) {
    const linkId = this.afdb.createPushId()

    this.showUploadFileModal(envId, linkId, "add")
  }

  showUploadFileModal(envId: string, linkId: string, type: "add" | "replace", link: any = null) {
    const fileId = this.afdb.createPushId()

    const dataModel = { linkPath: link ? link.linkPath : "", filename: null, file: null, type: type }
    const modalId = this.modalService.show({
      template: this.addLinkTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          fileChange: (event) => {
            const file: File = event.target.files[0]
            if (file) {
              dataModel.filename = file.name
              dataModel.file = file
            }
          },
          onLinkFileNameClick: (linkFile) => {
            if (!dataModel.file) {
              linkFile.click()
            }
          },
          deselectFile: (linkFile) => {
            dataModel.filename = ""
            dataModel.file = null
            linkFile.value = '';
          },
          add: () => {
            const filePath = `download_links/${envId}/${linkId}/${fileId}`
            this.uploadFile(filePath, dataModel.filename, dataModel.file)
              .then(() => this.afdb.object(`download_links/environments/${envId}/links/${linkId}`).set({ filename: dataModel.filename, linkPath: dataModel.linkPath, filePath: filePath }))
              .then(() => this.modalService.hide(modalId))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Upload Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
          },
          replace: async () => {
            const filePath = `download_links/${envId}/${linkId}/${fileId}`
            const oldFilePath = link.filePath

            // Upload new file first
            this.uploadFile(filePath, dataModel.filename, dataModel.file)
              .then(() => this.afdb.object(`download_links/environments/${envId}/links/${linkId}`).set({
                filename: dataModel.filename,
                linkPath: dataModel.linkPath,
                filePath: filePath
              }))
              // then try to remove the old file
              .then(() => {
                if (oldFilePath) {
                  this.afst.ref(oldFilePath).delete().toPromise()
                    .catch(err => console.log(err))
                }
              })
              .then(() => this.modalService.hide(modalId))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Upload Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
          }
        }
      }
    });
  }

  uploadFile(filePath: string, filename: string, file: File) {
    const uploadTask = this.afst.ref(filePath).put(file, { contentDisposition: contentDisposition(filename), contentType: file.type })

    const dataModel = { progress: 0 }
    const modalId = this.modalService.show({
      template: this.uploadTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          cancel: () => {
            uploadTask.cancel()
            this.modalService.hide(modalId)
          }
        }
      }
    })
    uploadTask.percentageChanges().subscribe(progress => { dataModel.progress = progress })
    return uploadTask
      .then(() => new Promise<void>(resolve => setTimeout(() => resolve(), 1000)))
      .then(() => this.modalService.hide(modalId))
      .catch(e => {
        this.modalService.hide(modalId)
        throw e
      })
  }

  deleteLnk(envId: string, lnk: any) {
    const modalId = this.modalService.show({
      template: this.deleteLinkTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          delete: () => {
            this.loaderService.show()
            let promise
            if (lnk.filePath) {
              promise = this.afst.ref(lnk.filePath).delete().toPromise()
                .then(() => this.afdb.object(`download_links/environments/${envId}/links/${lnk.id}`).remove())
            } else {
              promise = this.afdb.object(`download_links/environments/${envId}/links/${lnk.id}`).remove()
            }
            promise
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Delete Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
              .finally(() => {
                this.loaderService.hide()
                this.modalService.hide(modalId)
              })
          }
        }
      }
    })
  }

  downloadLnk(lnk: any) {
    this.loaderService.show()
    this.afst.ref(lnk.filePath).getDownloadURL().toPromise()
      .then(url => {
        const dataModel = { url: url }
        const modalId = this.modalService.show({
          template: this.downloadTemplate,
          context: {
            dataModel: dataModel,
            callbacks: {
              close: () => this.modalService.hide(modalId)
            }
          }
        })
      })
      .catch(error => {
        console.log(error)
        this.flashMessages.show('Download link cannot generated', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
      })
      .finally(() => this.loaderService.hide())
  }
}
